import { ModalFooter } from 'react-bootstrap';
import { useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Button from 'components/button/Button';
import ModalFormCancelButton from 'components/modal/form/ModalFormCancelButton';
import ModalFormSubmitButton from 'components/modal/form/ModalFormSubmitButton';
import Tooltip from 'components/tooltip/Tooltip';
import { ScheduledMessage } from 'models/automation/ScheduledMessages';
import { TriggerState } from 'models/automation/Triggers';
import useScheduledMessagesReactivateModal from 'pages/automations/scheduledMessages/modal/reactivate/useScheduledMessagesReactivateModal';
import useScheduledMessagesSendNowModal from '../sendNow/useScheduledMessagesSendNowModal';

interface ScheduledMessagesEditModalFooterProps {
  scheduledMessage: ScheduledMessage;
  closeModal: () => void;
}

const ScheduledMessagesEditModalFooter = ({
  scheduledMessage,
  closeModal,
}: ScheduledMessagesEditModalFooterProps) => {
  const { t } = useTranslation();
  const { isDirty, isSubmitting } = useFormState();

  const { openReactivateScheduledMessagesModal } =
    useScheduledMessagesReactivateModal();
  const { openSendNowScheduledMessagesModal } =
    useScheduledMessagesSendNowModal();

  const isCancelled = scheduledMessage.state === TriggerState.CANCELLED;
  const isScheduled = scheduledMessage.state === TriggerState.SCHEDULED;

  return (
    <ModalFooter>
      <ModalFormCancelButton closeOnClick={false} onClick={closeModal} />

      {isCancelled && (
        <Button
          $colorScheme="green"
          data-testid="modal-form-reactivate-button"
          onClick={() => {
            closeModal();
            openReactivateScheduledMessagesModal({ scheduledMessage });
          }}
        >
          {t('pageAutomations.scheduledMessages.modalReactivate.title')}
        </Button>
      )}

      {isScheduled && (
        <Button
          $colorScheme="green"
          data-testid="modal-form-send-now-button"
          disabled={isSubmitting || isDirty}
          onClick={() => {
            closeModal();
            openSendNowScheduledMessagesModal({ scheduledMessage });
          }}
        >
          {isDirty ? (
            <Tooltip
              id="send-now-disabled-tooltip"
              text={t(
                'pageAutomations.scheduledMessages.modalSendNow.sendNowDisabled',
              )}
            >
              {t('pageAutomations.scheduledMessages.modalSendNow.sendNow')}
            </Tooltip>
          ) : (
            t('pageAutomations.scheduledMessages.modalSendNow.sendNow')
          )}
        </Button>
      )}

      <ModalFormSubmitButton disabled={!isDirty}>
        {isSubmitting
          ? t('common.saving')
          : t('pageAutomations.scheduledMessages.modal.saveMessage')}
      </ModalFormSubmitButton>
    </ModalFooter>
  );
};

export default ScheduledMessagesEditModalFooter;
